import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';

import { isEqual as _isEqual } from 'lodash-es';

import { environment } from '@environment';

import type { AnywhereReportsListResponse } from '@types';

import * as pcacheActions from '../store/pcache/pcache.actions';
import * as pcacheSelectors from '../store/pcache/pcache.selectors';
import * as tempdataSelectors from '../store/tempdata/tempdata.selectors';
import { calculateFields } from './report-list/report-list-grid/report-list-grid.columns';
import * as reportActions from './store/report.actions';
import * as reportSelectors from './store/report.selectors';

@Injectable()
export class ReportService {
  public rlShowing$: Observable<any> = this.store.select(reportSelectors.selectReportListShowing);
  public showReportParameters$ = new BehaviorSubject(false);
  public rlFilter$ = this.store
    .select(reportSelectors.selectReportListFilter)
    .pipe(distinctUntilChanged((a: any, b: any) => _isEqual(a, b)));

  public rlColPref$ = this.store.select(pcacheSelectors.selectReportListColPref).pipe(
    tap((pref) => {
      if (!pref) {
        this.store.dispatch(new pcacheActions.ReInit());
      }
    }),
    filter((pref) => !!pref)
  );

  public rlGridData$ = this.store.select(tempdataSelectors.selectReportListGridData).pipe(map((rowdata) => calculateFields(rowdata)));

  public rlSelectedRow$ = this.store.select(reportSelectors.selectReportListSelectedRow);

  constructor(private http: HttpClient, private store: Store) {}

  public setReportListShowing(area: string) {
    this.store.dispatch(new reportActions.ReportListSetShowing(area));
  }

  public getReportList(): Observable<AnywhereReportsListResponse> {
    return this.http.get<AnywhereReportsListResponse>(environment.reportListEndpoint, { withCredentials: true }).pipe();
  }

  getReportParameters(reportName: string): Observable<any> {
    return this.http.post(environment.reportGetParametersEndpoint, { reportName }, { withCredentials: true }).pipe();
  }

  setReportParameters(requestId: string, reportName: string, params: any[]): Observable<any> {
    return this.http.post(environment.reportSetParametersEndpoint, { requestId, reportName, params }, { withCredentials: true }).pipe();
  }

  generateReport(requestId: string, reportName: string, format: string): Observable<any> {
    return this.http.post(environment.reportGenerateEndpoint, { requestId, reportName, format }, { withCredentials: true }).pipe();
  }

  downloadReport(requestId: string, reportName: string, format: string) {
    return this.http
      .post(environment.reportDownloadEndpoint, { requestId, reportName, format }, { withCredentials: true, responseType: 'blob' })
      .pipe(map((x) => x));
  }
}
